

import { lazy, Suspense, useEffect } from 'react';

/// Components
import Index from './jsx';
import { Route, Routes} from 'react-router-dom';


/* theme-override.css was one 7,500-line file; it is now split by concern.
   The order below is the cascade - it reproduces the old file's source
   order exactly, so nothing resolves differently. shell-geometry.css is
   last on purpose: its .header padding-left rules have to beat the ones
   in responsive.css. */
/* Structural shell: positions the rail, topbar, content area and footer.
   It replaces the layout that style.css used to provide and paints
   nothing, so the theme files below still own every colour. */
import './css/layout.css';
import './css/grid.css';

import './css/theme/tokens.css';
/* The palette's thirteen --pg-* values. Loaded after tokens.css so a
   palette can mirror onto --ground/--paper; ThemeContext then writes the
   chosen set inline on :root, which beats both regardless of order. */
import './css/theme/palettes.css';
/* The mode's ink (--ink-*) and --on-accent, day on :root and night under
   [data-mode="night"]. Beside palettes.css because the two are the two
   halves of the theme: the palette owns decoration, the mode legibility. */
import './css/theme/modes.css';
/* Base styling for react-bootstrap's <Form.Control>/<Form.Select>/
   <Form.Label>. They render .form-control / .form-select / .form-label
   and ship no CSS; Bootstrap used to supply it and this project dropped
   Bootstrap, so every field in the app was a bare browser input. Placed
   after tokens (it reads them) and before the theme sheets, so any
   page-level field styling still overrides it. */
import './css/theme/forms.css';
import './css/theme/shell.css';
import './css/theme/patterns.css';
import './css/theme/responsive.css';
import './css/theme/popups.css';
import './css/theme/cursors.css';
import './css/theme/shell-geometry.css';
import SessionExpired from './jsx/components/common/SessionExpired.jsx';
import ToastHost from './jsx/components/common/Toast/ToastHost.jsx';
import ConfirmHost from './jsx/components/common/Confirm/ConfirmHost.jsx';
import WaypointProvider from './jsx/components/common/waypoint/WaypointProvider.jsx';
import FeedbackWidget from './jsx/components/Student/Feedback/FeedbackWidget.jsx';
import StaffFeedbackWidget from './jsx/components/Feedback/StaffFeedbackWidget.jsx';
import GlobalLoader from './jsx/components/common/Loader/GlobalLoader.jsx';
import { useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';

const SignUp = lazy(() => import('./jsx/pages/Registration'));
const Login = lazy(() => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
  });
});
const ForgotPassword = lazy(() => import('./jsx/pages/ForgotPassword'));
const ChangePassword = lazy(() => import('./jsx/pages/ChangePassword'));
const ResetPassword = lazy(() => import('./jsx/pages/ResetPassword'));

function App(props) {
  const { user } = useAuth();

  // Global Google OAuth Token Handler
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('google_oauth') === 'success') {
      const accessToken = searchParams.get('accessToken');
      const refreshToken = searchParams.get('refreshToken');

      if (accessToken) {
        localStorage.setItem('googleAccessToken', accessToken);
      }
      if (refreshToken) {
        localStorage.setItem('googleRefreshToken', refreshToken);
      }

      // Clean up URL but keep current path
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []); // Run once on mount

  let routeblog = (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Login />} />
      <Route path="/page-register" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/change-password" element={<ChangePassword />} />
      {/* Handles https://lms.sevenmentor.io/reset-password/TOKEN */}
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="*" element={<Login />} />
    </Routes>
  );
  if (user) {
    return (
      <SocketProvider>
        {/* Covers the whole app when a request comes back 401. Only in
            the signed-in tree -- the sign-in screens have no session to
            lose, and a 401 there is a wrong password. */}
        <SessionExpired />
        {/* The app's one notifier. It was 25 separate <ToastContainer>s,
            one per page that remembered to add it -- so a message raised
            from a page without one went nowhere, and with two mounted it
            could land in either. Mounted in BOTH branches below: a failed
            sign-in has to be able to say so too. */}
        <ToastHost />
        {/* The app's one "are you sure?". Every delete, remove and
            archive confirmation goes through common/Confirm/confirm.js
            and is drawn here, in the house popup frame -- they were 52
            SweetAlert dialogs with their own orange icon and colours. */}
        <ConfirmHost />
        {/* The app's one loading overlay. Raised by the axios
            interceptor, never by a page -- see Loader/busy.js for what
            counts as blocking and why pagination does not. */}
        <GlobalLoader />
        {/* Rate-your-classes, bottom right. It renders nothing at all for
            anyone who is not a student, so it is mounted here rather than
            on the student dashboard: a class is marked while the student
            is on whatever page they happen to be on, and the widget has
            to be there to say so. */}
        <FeedbackWidget />
        {/* New session feedback used to announce itself here as a stack
            of cards (Feedback/FeedbackFeed). They polled on mount, so
            signing in threw a popup into the corner before anyone had
            asked for one -- and they were the third place the same rating
            was already being reported: the bell raises it, and the
            standing overlay below carries the count of what is unread.
            The cards are gone; the count is the announcement. */}
        {/* The nav bridge. One modal for the whole signed-in app, so any
            page can open any entity -- and drill from one to the next --
            without importing the page that entity normally lives on.
            Inside the router, because the stack it holds lives in the
            URL; only in this branch, because there is nothing to look at
            before sign-in. */}
        <WaypointProvider>
          {/* The standing feedback overlay, for staff: the launcher
              carries how many ratings are still to review, and the panel
              is where they are read. It renders for nobody else, so only
              one launcher is ever in the corner.

              INSIDE the provider, not beside the others: it draws the same
              FeedbackList the page does, and a row there opens the batch
              through the waypoint. It portals to document.body regardless,
              so this changes where it sits in the React tree -- which is
              what context follows -- and not where it sits on screen. */}
          <StaffFeedbackWidget />
          <Suspense
            fallback={
              <div id="preloader">
                <div className="sk-three-bounce">
                  <div className="sk-child sk-bounce1"></div>
                  <div className="sk-child sk-bounce2"></div>
                  <div className="sk-child sk-bounce3"></div>
                </div>
              </div>
            }
          >
            <Index />
          </Suspense>
        </WaypointProvider>
      </SocketProvider>
    );
  } else {
    return (
      <div className="vh-100">
        <ToastHost />
        <GlobalLoader />
        <Suspense
          fallback={
            <div id="preloader">
              <div className="sk-three-bounce">
                <div className="sk-child sk-bounce1"></div>
                <div className="sk-child sk-bounce2"></div>
                <div className="sk-child sk-bounce3"></div>
              </div>
            </div>
          }
        >
          {routeblog}
        </Suspense>
      </div>
    );
  }
}

export default App;
